/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { LexicalComposer } from "@lexical/react/LexicalComposer";

import { Template } from "@/features/templates/types";
import { useEditorStore } from "@/stores/editor";
import * as Sentry from "@sentry/react";
import { TextNode } from "lexical";
import { TemplateEditor } from "./TemplateEditor";
import { SettingsContext } from "./context/SettingsContext";
import { SharedAutocompleteContext } from "./context/SharedAutocompleteContext";
import { SharedHistoryContext } from "./context/SharedHistoryContext";
import "./index.css";
import PlaygroundNodes from "./nodes/PlaygroundNodes";
import { ExtendedTextNode } from "./plugins/ExtendedTextNodePlugin/ExtendedTextNodePlugin";
import { TableContext } from "./plugins/TablePlugin";
import PlaygroundEditorTheme from "./themes/PlaygroundEditorTheme";

console.warn(
  "If you are profiling the playground app, please ensure you turn off the debug view. You can disable it by pressing on the settings control in the bottom-left of your screen and toggling the debug view setting."
);

function TemplateLexicalInstance({
  templateMetadata,
  isReadOnly,
}: {
  templateMetadata: Template;
  isReadOnly: boolean;
}): JSX.Element {
  const { editor: editorStore } = useEditorStore();

  const initialConfig = {
    namespace: "Playground",
    nodes: [
      ...PlaygroundNodes,
      ExtendedTextNode,
      {
        replace: TextNode,
        with: (node: TextNode) => new ExtendedTextNode(node.__text),
      },
    ],
    onError: (error: Error) => {
      editorStore.instance?.focus();
      if (process.env.NODE_ENV === "development") {
        console.error(error);
      }
      Sentry.captureException(error);
    },
    editable: !isReadOnly,
    theme: PlaygroundEditorTheme,
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <SharedHistoryContext>
        <TableContext>
          <SharedAutocompleteContext>
            <div className="editor-shell flex flex-col w-full h-full">
              <TemplateEditor
                templateMetadata={templateMetadata}
                isReadOnly={isReadOnly}
              />
            </div>
          </SharedAutocompleteContext>
        </TableContext>
      </SharedHistoryContext>
    </LexicalComposer>
  );
}

export const TemplateEditorContainer = ({
  templateMetadata,
  isReadOnly,
}: {
  templateMetadata: Template;
  isReadOnly: boolean;
}) => {
  return (
    <SettingsContext>
      <TemplateLexicalInstance
        templateMetadata={templateMetadata}
        isReadOnly={isReadOnly}
      />
    </SettingsContext>
  );
};
